import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api'
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    // Handle pending approval - redirect to waiting page
    if (error.response?.status === 403 && error.response?.data?.code === 'PENDING_APPROVAL') {
      window.location.href = '/pending-approval';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const auth = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  // Entrada amb codi imprès (examen). Públiques: no porten token.
  comprovaCodi: (code) => api.post('/auth/code/check', { code }),
  entraAmbCodi: (code) => api.post('/auth/code', { code }),
  register: (data) => api.post('/auth/register', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  refreshToken: () => api.post('/auth/refresh'),
};

// Users endpoints
export const users = {
  getAll: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  deactivate: (id) => api.delete(`/users/${id}`),

  // Esborrat DEFINITIU, que no és el mateix que `deactivate`. Sense `dades`
  // només esborra els comptes que no tenen feina; per endur-se'n un amb
  // exercicis cal {ambFeina: true, confirmacio: '<nom exacte>'}. El cos va dins
  // de `data` perquè axios no l'envia com a segon paràmetre en un DELETE.
  esborrar: (id, dades) => api.delete(`/users/${id}/definitiu`, { data: dades || {} }),
  // Pending users (approval workflow)
  getPending: () => api.get('/users/pending'),
  getPendingCount: () => api.get('/users/pending/count'),
  approve: (id) => api.post(`/users/${id}/approve`),
  reject: (id) => api.post(`/users/${id}/reject`),
};

// Projects endpoints
export const projects = {
  getAll: (params) => api.get('/projects', { params }),
  create: (data) => api.post('/projects', data),
  getById: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.put(`/projects/${id}`, data),
  archive: (id) => api.delete(`/projects/${id}`),
  publish: (id) => api.post(`/projects/${id}/publish`),
};

// Cases endpoints
export const cases = {
  getAll: (params) => api.get('/cases', { params }),
  getMyCases: () => api.get('/cases/my'),
  create: (data) => api.post('/cases', data),
  getById: (id) => api.get(`/cases/${id}`),
  update: (id, data) => api.put(`/cases/${id}`, data),
  delete: (id) => api.delete(`/cases/${id}`),
  // Teams for a case
  getTeams: (caseId) => api.get(`/cases/${caseId}/teams`),
  createTeam: (caseId, data) => api.post(`/cases/${caseId}/teams`, data),
  // Assessments for a case
  getAssessments: (caseId, params) => api.get(`/cases/${caseId}/assessments`, { params }),
  startAssessment: (caseId, data) => api.post(`/cases/${caseId}/assessments/start`, data || {}),
  // Evidence for a case
  getEvidence: (caseId) => api.get(`/cases/${caseId}/evidence`),
  getEvidenceById: (caseId, id) => api.get(`/cases/${caseId}/evidence/${id}`),
  createEvidence: (caseId, data) => api.post(`/cases/${caseId}/evidence`, data),
  updateEvidence: (caseId, id, data) => api.put(`/cases/${caseId}/evidence/${id}`, data),
  deleteEvidence: (caseId, id) => api.delete(`/cases/${caseId}/evidence/${id}`),
  // Actor templates for a case
  getActorTemplates: (caseId) => api.get(`/cases/${caseId}/actor-templates`),
  createActorTemplate: (caseId, data) => api.post(`/cases/${caseId}/actor-templates`, data),
  updateActorTemplate: (caseId, id, data) => api.put(`/cases/${caseId}/actor-templates/${id}`, data),
  deleteActorTemplate: (caseId, id) => api.delete(`/cases/${caseId}/actor-templates/${id}`),
  // Enrollments for a case
  getEnrollments: (caseId) => api.get(`/cases/${caseId}/enrollments`),
  createEnrollment: (caseId, data) => api.post(`/cases/${caseId}/enrollments`, data),
  revokeEnrollment: (caseId, id) => api.put(`/cases/${caseId}/enrollments/${id}/revoke`),
  deleteEnrollment: (caseId, id) => api.delete(`/cases/${caseId}/enrollments/${id}`),
  checkEnrollmentStatus: (caseId) => api.get(`/cases/${caseId}/enrollment-status`),
  // Invitation codes for a case
  getInvitations: (caseId) => api.get(`/cases/${caseId}/invitations`),
  // Grups d'examen amb codi imprès
  getGrups: (caseId) => api.get(`/cases/${caseId}/grups`),
  preparaGrups: (caseId, quantitat) => api.post(`/cases/${caseId}/grups`, { quantitat }),
  regeneraCodiGrup: (caseId, userId) => api.post(`/cases/${caseId}/grups/${userId}/codi`),
  revocaCodiGrup: (caseId, userId) => api.delete(`/cases/${caseId}/grups/${userId}/codi`),
  revocaTotsElsCodis: (caseId) => api.post(`/cases/${caseId}/grups/revocar-tots`),
  tancaSessioGrup: (caseId, userId) => api.post(`/cases/${caseId}/grups/${userId}/tancar-sessio`),
};

// Teams endpoints
export const teams = {
  getAll: () => api.get('/teams'),
  getMyTeams: () => api.get('/teams/my'),
  getById: (id) => api.get(`/teams/${id}`),
  delete: (id) => api.delete(`/teams/${id}`),
  addMember: (teamId, data) => api.post(`/teams/${teamId}/members`, data),
  removeMember: (teamId, userId) => api.delete(`/teams/${teamId}/members/${userId}`),
};

// Assessments endpoints
export const assessments = {
  getMyAssessments: (params) => api.get('/assessments/my', { params }),
  getById: (id) => api.get(`/assessments/${id}`),
  update: (id, data) => api.put(`/assessments/${id}`, data),
  submit: (id) => api.post(`/assessments/${id}/submit`),
  timeExpired: (id) => api.post(`/assessments/${id}/time-expired`),
  abandon: (id) => api.post(`/assessments/${id}/abandon`),
  // Instructor controls
  pause: (id) => api.post(`/assessments/${id}/pause`),
  resume: (id) => api.post(`/assessments/${id}/resume`),
  unlock: (id) => api.post(`/assessments/${id}/unlock`),
  reset: (id) => api.post(`/assessments/${id}/reset`),
  // Items
  getItems: (assessmentId) => api.get(`/assessments/${assessmentId}/items`),
  createItem: (assessmentId, data) => api.post(`/assessments/${assessmentId}/items`, data),
  updateItem: (assessmentId, itemId, data) => api.put(`/assessments/${assessmentId}/items/${itemId}`, data),
  deleteItem: (assessmentId, itemId) => api.delete(`/assessments/${assessmentId}/items/${itemId}`),
  // Connections
  getConnections: (assessmentId) => api.get(`/assessments/${assessmentId}/connections`),
  createConnection: (assessmentId, data) => api.post(`/assessments/${assessmentId}/connections`, data),
  deleteConnection: (assessmentId, connectionId) => api.delete(`/assessments/${assessmentId}/connections/${connectionId}`),
  // Chat
  getChatHistory: (assessmentId, params) => api.get(`/assessments/${assessmentId}/chat`, { params }),
  // Actors
  getActors: (assessmentId) => api.get(`/assessments/${assessmentId}/actors`),
  createActor: (assessmentId, data) => api.post(`/assessments/${assessmentId}/actors`, data),
  updateActor: (assessmentId, actorId, data) => api.put(`/assessments/${assessmentId}/actors/${actorId}`, data),
  deleteActor: (assessmentId, actorId) => api.delete(`/assessments/${assessmentId}/actors/${actorId}`),
  // ACH
  getACH: (assessmentId) => api.get(`/assessments/${assessmentId}/ach`),
  createHypothesis: (assessmentId, data) => api.post(`/assessments/${assessmentId}/ach/hypotheses`, data),
  updateHypothesis: (assessmentId, hId, data) => api.put(`/assessments/${assessmentId}/ach/hypotheses/${hId}`, data),
  deleteHypothesis: (assessmentId, hId) => api.delete(`/assessments/${assessmentId}/ach/hypotheses/${hId}`),
  updateACHCell: (assessmentId, data) => api.put(`/assessments/${assessmentId}/ach/cells`, data),
  // Notes
  getNotes: (assessmentId) => api.get(`/assessments/${assessmentId}/notes`),
  createNote: (assessmentId, data) => api.post(`/assessments/${assessmentId}/notes`, data),
  updateNote: (assessmentId, noteId, data) => api.put(`/assessments/${assessmentId}/notes/${noteId}`, data),
  deleteNote: (assessmentId, noteId) => api.delete(`/assessments/${assessmentId}/notes/${noteId}`),
  // Threat Assessment
  getThreatAssessment: (assessmentId) => api.get(`/assessments/${assessmentId}/threat-assessment`),
  updateThreatAssessment: (assessmentId, data) => api.put(`/assessments/${assessmentId}/threat-assessment`, data),
  submitThreatAssessment: (assessmentId) => api.post(`/assessments/${assessmentId}/threat-assessment/submit`),
};

// Grades endpoints
export const grades = {
  getMyGrades: () => api.get('/grades/my'),
  getForAssessment: (assessmentId) => api.get(`/grades/assessments/${assessmentId}`),
  submit: (assessmentId, data) => api.post(`/grades/assessments/${assessmentId}`, data),
  getForProject: (projectId) => api.get(`/grades/projects/${projectId}`),
};

// Notifications endpoints
export const notifications = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread'),
  markAsRead: (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Invitations endpoints
export const invitations = {
  getAll: (params) => api.get('/invitations', { params }),
  create: (data) => api.post('/invitations', data),
  verify: (code) => api.post('/invitations/verify', { code }),
  join: (code) => api.post('/invitations/join', { code }),
  getUses: (id) => api.get(`/invitations/${id}/uses`),
  deactivate: (id) => api.put(`/invitations/${id}/deactivate`),
  delete: (id) => api.delete(`/invitations/${id}`),
};

export default api;
